/**
 * Date formatting utilities for consistent date and time display across the application.
 *
 * These utilities use the Intl.DateTimeFormat API to format dates in a user-friendly way,
 * respecting the user's locale while maintaining consistency across the application.
 */

/**
 * Formats a date string to a medium-length date and time format.
 *
 * Example output: "Jan 15, 2024, 2:30:45 PM"
 *
 * @param dateString - The ISO 8601 date string to format (e.g., "2024-01-15T14:30:45Z")
 * @returns Formatted date string in medium format, or '-' if the input is undefined or invalid
 *
 * @example
 * ```typescript
 * formatDateTime("2024-01-15T14:30:45Z")
 * // Returns: "Jan 15, 2024, 2:30:45 PM"
 *
 * formatDateTime(undefined)
 * // Returns: "-"
 * ```
 */
export function formatDateTime(dateString?: string): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string provided to formatDateTime: ${dateString}`);
      return '-';
    }

    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

/**
 * Formats a date string to a short date and time format.
 *
 * Example output: "1/15/24, 2:30 PM"
 *
 * @param dateString - The ISO 8601 date string to format (e.g., "2024-01-15T14:30:45Z")
 * @returns Formatted date string in short format, or '-' if the input is undefined or invalid
 *
 * @example
 * ```typescript
 * formatDateTimeShort("2024-01-15T14:30:45Z")
 * // Returns: "1/15/24, 2:30 PM"
 *
 * formatDateTimeShort(undefined)
 * // Returns: "-"
 * ```
 */
export function formatDateTimeShort(dateString?: string): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string provided to formatDateTimeShort: ${dateString}`);
      return '-';
    }

    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

/**
 * Formats a date string using the browser's default locale string formatting.
 *
 * Example output: "1/15/2024, 2:30:45 PM"
 *
 * @param dateString - The ISO 8601 date string to format (e.g., "2024-01-15T14:30:45Z")
 * @returns Formatted date string using toLocaleString(), or '-' if the input is undefined or invalid
 *
 * @example
 * ```typescript
 * formatDateLocale("2024-01-15T14:30:45Z")
 * // Returns: "1/15/2024, 2:30:45 PM"
 *
 * formatDateLocale(undefined)
 * // Returns: "-"
 * ```
 */
export function formatDateLocale(dateString?: string): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string provided to formatDateLocale: ${dateString}`);
      return '-';
    }

    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}
